import { makeStyles } from "tss-react/mui";
import { Grid, Typography, Chip, CircularProgress, Box, List } from "@mui/material";
import { useTranslation } from "react-i18next";
import ResponsivePage from "../../common/core/ResponsivePage";
import config from "../../../config";
import { useStation } from "../../../providers/StationProvider";
import JoinNowCard from "./common/JoinNowCard";
import { LinkOff as LinkOffIcon, Circle as CircleIcon, Logout as LogoutIcon, Edit as EditIcon } from "@mui/icons-material";
import { useCallback, useState } from "react";
import { Task } from "@emberly/zenith-client";
import AlertDialog from "../../common/inputs/AlertDialog";
import SectionItem from "../../common/inputs/SectionItem";
import { ToggleSurface } from "../../common/core/Surface";
import NetworkProfileDialog from "./common/NetworkProfileDialog";
import ConfirmDialog from "../../common/inputs/ConfirmDialog";
import { useDevice } from "../../../providers/DeviceProvider";
import { StationItem } from "../missionrequests/common/NetworkStationSearch";


const useStyles = makeStyles()(
  (theme) => ({
    nonMemberChip: {

    },
    memberChip: {

    },
    link: {
      color: theme.palette.info.main
    },
    progressBox: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      paddingTop: "160px"
    },
    listEdit: {
      marginTop: "-24px",
      width: "100%",
      "& a": {
        textDecoration: "none",
        color: "inherit"
      },
      "& .MuiListItemText-primary": {
        fontWeight: 500
      }
    },
    list: {

      marginTop: "-8px",
      width: "100%",
      "& a": {
        textDecoration: "none",
        color: "inherit"
      },
      "& .MuiListItemText-primary": {
        fontWeight: 500
      }
    }
  })
);

export default function AssistNetworkPage() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { station, updateStation } = useStation();
  const { slim } = useDevice();

  const [justJoined, setJustJoined] = useState(null);

  const onJoined = useCallback(async () => {
    try {
      const t = new Task();
      setJustJoined(t);
      await t.wait();
    } catch (err) {
      console.log(err);
    } finally {
      setJustJoined(null);
    }
  }, []);

  const [editAction, setEditAction] = useState(null);

  const onEdit = useCallback(async () => {
    try {
      let t = new Task();
      setEditAction(t);
      let data = await t.wait();
      setEditAction(null);
      await updateStation({ ...data });
    } catch (err) {
      console.log(err);
      setEditAction(null);
    }
  }, [updateStation]);

  const [leaveAction, setLeaveAction] = useState(null);

  const onLeave = useCallback(async () => {
    try {
      let t = new Task();
      setLeaveAction(t);
      await t.wait();
      await updateStation({ isNetworkMember: false });
    } catch (err) {
      console.log(err);
    } finally {
      setLeaveAction(null);
    }
  }, [updateStation]);

  const widget = (
    !station ?
      undefined :
      station?.isNetworkMember ?
        <Chip label={t("assistNetwork:member")} icon={<CircleIcon />} className={classes.memberChip} color="success" variant="outlined" /> :
        <Chip label={t("assistNetwork:nonMember")} icon={<LinkOffIcon />} className={classes.nonMemberChip} />
  );

  return (
    <ResponsivePage
      title={t("assistNetwork:title")}
      maxWidth={!station?.isNetworkMember ? config.pageSize.compact : config.pageSize.small}
      widget={
        !slim ? widget : undefined
      }
    >
      {
        slim ?
          (
            <Grid item xs={12}>
              <Box sx={{ marginBottom: 1.5 }}>
                {widget}
              </Box>
            </Grid>
          ) : undefined
      }

      {
        !station?.isNetworkMember ? (
          <Grid item xs={12}>
            {!!station ? (
              <JoinNowCard onJoined={onJoined} />
            ) : (
              <Box className={classes.progressBox}>
                <CircularProgress variant="indeterminate" />
              </Box>
            )}
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography variant={slim ? "body2" : "body1"} color="textSecondary">
                {t("assistNetwork:description")}
                <br />
                <a href="https://help.getassist.app/nb/articles/12470322" target="_blank" className={classes.link}>{t("assistNetwork:link")}</a>
              </Typography>
            </Grid>


            <ToggleSurface xs={12}>

              <List className={classes.listEdit}>
                <SectionItem
                  onClick={onEdit}
                  disableGutters
                  icon={<EditIcon />}
                  primary={t("assistNetwork:sections:profileTitle")}
                  secondary={t("assistNetwork:sections:profileDescription")}
                  disableDivider
                />
              </List>

              {
                !!station ? (
                  <StationItem station={station} expanded onExpand={onEdit} />
                ) : null
              }

            </ToggleSurface>


            <ToggleSurface xs={12}>
              <List className={classes.list}>

                <SectionItem
                  to="agreements"
                  primary={t("assistNetwork:sections:agreementsTitle")}
                  secondary={t("assistNetwork:sections:agreementsDescription")}
                  disabled
                  disableGutters
                />

                <SectionItem
                  to="statistics"
                  primary={t("assistNetwork:sections:statisticsTitle")}
                  secondary={t("assistNetwork:sections:statisticsDescription")}
                  disabled
                  disableGutters
                />

                <SectionItem
                  onClick={onLeave}
                  primary={t("assistNetwork:sections:leaveNetworkTitle")}
                  secondary={t("assistNetwork:sections:leaveNetworkDescription")}
                  icon={<LogoutIcon color="warning" />}
                  disableGutters
                />

              </List>
            </ToggleSurface>
          </>
        )
      }

      {
        !!justJoined ? (
          <AlertDialog
            maxWidth="sm"
            color="primary"
            task={justJoined}
            title={t("assistNetwork:joinedDialog:title")}
            description={t("assistNetwork:joinedDialog:description")}
            confirmText={t("assistNetwork:joinedDialog:action")}
          />
        ) : null
      }

      {!!editAction ? (
        <NetworkProfileDialog task={editAction} />
      ) : null}

      {
        !!leaveAction ? (
          <ConfirmDialog
            color="error"
            task={leaveAction}
            title={t("assistNetwork:leaveDialog:title")}
            description={t("assistNetwork:leaveDialog:description")}
            confirmText={t("assistNetwork:leaveDialog:action")}
          />
        ) : null
      }

    </ResponsivePage>
  );
}
